import { useEffect, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { PHONE_TEL, PHONE_DISPLAY } from "../content/siteContent";
import { IconChevronDown, IconMenu, IconNav, IconPhone } from "./Icons";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeToggle from "./ThemeToggle";
import MobileNavigation from "./MobileNavigation";

const SiteHeader = () => {
    const { t, lang } = useLanguage();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [active, setActive] = useState("");

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActive(entry.target.id);
                });
            },
            { rootMargin: "-40% 0px -55% 0px" },
        );
        t.nav.flatMap((item) => item.children || [item]).forEach((item) => {
            const el = document.getElementById(item.id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, [t.nav]);

    return (
        <header
            className={`fixed inset-x-0 top-0 z-40 border-b backdrop-blur-xl transition-[background-color,box-shadow] duration-500 ${
                scrolled ? "border-ink/10 bg-sand/90 shadow-[0_8px_30px_-18px_rgba(0,77,77,0.35)]" : "border-ink/5 bg-sand/70"
            }`}
            data-testid="site-header"
        >
            <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between gap-3 px-4 sm:px-6 md:h-20 lg:px-8">
                <a href="#top" className="flex min-h-11 items-center gap-3" data-testid="header-logo" aria-label={t.brandFull}>
                    <img src="/assets/thera-symbol-original.png" alt="" className="h-11 w-11 object-contain" />
                    <span className="leading-none">
                        <span
                            className={`block font-display font-semibold text-pine ${
                                lang === "en" ? "text-[1.05rem] tracking-[-0.04em] sm:text-[1.15rem]" : "text-lg"
                            }`}
                        >
                            {t.brand}
                        </span>
                        <span
                            className={`block text-[9px] font-semibold uppercase tracking-[0.24em] ${
                                lang === "en" ? "text-gold-600" : "text-gold-600"
                            }`}
                        >
                            {t.brandLatin}
                        </span>
                    </span>
                </a>

                <nav aria-label="Main" className="hidden flex-1 justify-center lg:flex">
                    <ul className="flex items-center gap-1">
                        {t.nav.map((item) => {
                            const links = item.children || [item];
                            const isActive = links.some((link) => active === link.id);
                            return (
                            <li key={item.id} className="group relative">
                                <a
                                    href={`#${links[0].id}`}
                                    data-testid={`nav-link-${item.id}`}
                                    aria-current={isActive ? "true" : undefined}
                                    aria-haspopup={item.children ? "true" : undefined}
                                    className={`relative inline-flex min-h-10 items-center gap-1 px-2.5 py-2 text-sm font-medium tracking-[-0.01em] whitespace-nowrap transition-all duration-300 xl:px-3.5 xl:text-[0.90rem] ${
                                        isActive
                                            ? "text-pine after:absolute after:bottom-1 after:start-2 after:h-px after:w-[calc(100%-1rem)] after:bg-gold-500 xl:after:start-3 xl:after:w-[calc(100%-1.5rem)]"
                                            : "text-ink/80 after:absolute after:bottom-1 after:start-2 after:h-px after:w-0 after:bg-gold-500 after:transition-all after:duration-300 hover:text-pine hover:after:w-[calc(100%-1rem)] xl:hover:after:w-[calc(100%-1.5rem)]"
                                    }`}
                                >
                                    {item.label}
                                    {item.children && <IconChevronDown className="transition-transform duration-300 group-hover:rotate-180 group-focus-within:rotate-180" />}
                                </a>
                                {item.children && (
                                    <div className="invisible absolute start-0 top-full z-50 w-60 translate-y-2 rounded-2xl border border-ink/10 bg-surface p-2 opacity-0 shadow-[0_20px_45px_-24px_rgba(0,77,77,0.55)] transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
                                        {links.map((link) => (
                                            <a
                                                key={link.id}
                                                href={`#${link.id}`}
                                                data-testid={`nav-sub-link-${link.id}`}
                                                className="flex min-h-10 items-center rounded-xl px-3 text-sm text-ink/80 transition-colors duration-200 hover:bg-gold-100 hover:text-pine"
                                            >
                                                <IconNav name={link.icon} className="me-2.5 h-4 w-4 shrink-0 text-gold-600" />
                                                {link.label}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </li>
                            );
                        })}
                    </ul>
                </nav>

                <div className="flex shrink-0 items-center gap-1.5 sm:gap-2 xl:gap-3">
                    <a
                        href={`tel:${PHONE_TEL}`}
                        data-testid="header-phone-link"
                        className="hidden min-h-10 items-center gap-1.5 rounded-full border border-pine/20 px-3 text-xs font-semibold text-pine whitespace-nowrap transition-colors duration-300 hover:border-gold-500 hover:text-gold-600 xl:inline-flex xl:min-h-11 xl:gap-2 xl:px-4 xl:text-sm"
                    >
                        <IconPhone className="h-3.5 w-3.5 xl:h-4 xl:w-4" />
                        <span dir="ltr">{PHONE_DISPLAY}</span>
                    </a>
                    <a
                        href="#appointment"
                        data-testid="header-book-cta"
                        className="hidden min-h-10 items-center rounded-full bg-pine px-3.5 text-xs font-semibold text-cream shadow-[0_10px_25px_-12px_rgba(0,77,77,0.6)] whitespace-nowrap transition-[background-color,transform] duration-300 hover:bg-pine-600 hover:-translate-y-0.5 active:scale-95 sm:inline-flex xl:min-h-11 xl:px-5 xl:text-sm"
                    >
                        {t.header.book}
                    </a>
                    <LanguageSwitcher className="hidden sm:inline-flex" />
                    <ThemeToggle />
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        aria-label={t.header.openMenu}
                        aria-expanded={open}
                        data-testid="mobile-menu-button"
                        className="flex min-h-11 min-w-11 items-center justify-center rounded-full text-pine transition-colors duration-300 hover:bg-gold-100 lg:hidden"
                    >
                        <IconMenu />
                    </button>
                </div>
            </div>
            <MobileNavigation open={open} onClose={() => setOpen(false)} />
        </header>
    );
};

export default SiteHeader;
